import { t } from '@lingui/macro';

import Panel from '../../page-framework/panel/PanelDecorator';
import {
    PANEL_CATEGORIES,
    PANEL_PRIORITIES,
    STATUS_LIST_PANEL_PREVIEW_IMAGE,
} from '../../page-framework/panel/PanelRegistry';
import { panelPropTypes, panelPropDefaults } from '../../page-framework/panel/PanelPropTypes';
import { COLUMN_TYPES } from '../../data-table/DataTableColumns';
import { useQueryLoader } from '../../data-table/Loaders';
import { filterId as radioFilterId } from '../../filters/radio-filter/RadioFilter';
import ReportingDataTable from '../../data-table/ReportingDataTable';
import { listTooltip } from './funcs/IpmList';
import { DEFAULT_INVALID_VALUE } from '../../../utilities/FormattingUtilities';
import {
    accessPointFilterId,
    radioBandFilterId,
    siteFilterId,
    statusFilterId,
    timeRangeFilterId,
    wirelessControllerFilterId,
} from '../../filters';

const title = t`Radios`;

const dataSource = {
    url: '/query/table',
    params: {
        type: 'radio',
        entityFields: ['name', 'bssidList', 'band', 'location'],
        metrics: ['status', 'retryStatus', 'errorFrameStatus', 'channels', 'channelUtilizationStatus'],
        trends: ['status'],
        renderAsEntity: true,
    },
};

const columns = [
    {
        type: COLUMN_TYPES.DRILLINLINK,
        title: t`Name`,
        sortable: true,
        mapping: 'radio.name',
        getTooltip: listTooltip('radio.name'),
        width: 200,
        link: {
            target: '../radio-detail',
            mappedFilterParams: {
                [radioFilterId]: 'radio',
            },
        },
    },
    {
        type: COLUMN_TYPES.TEXT,
        title: t`Sites`,
        sortable: false,
        mapper: (row) => {
            const sites = row.radio.sites || [];
            if (sites.length === 0) return DEFAULT_INVALID_VALUE;
            return sites.map((site) => site.name).join(', ');
        },
        width: 120,
    },
    {
        type: COLUMN_TYPES.TEXT,
        title: t`Location`,
        sortable: true,
        mapping: 'radio.location',
        width: 120,
    },
    {
        type: COLUMN_TYPES.TEXT,
        title: t`Channels`,
        sortable: true,
        sortProperty: 'channels',
        mapper: (row) => {
            const channels = row.channels || [];
            if (channels.length === 0) return DEFAULT_INVALID_VALUE;
            return channels.map((channel) => channel.channelNumber).join(', ');
        },
        width: 110,
    },
    {
        type: COLUMN_TYPES.STATUS,
        title: t`Channel Util.`,
        sortable: true,
        mapping: 'channelUtilizationStatus',
        width: 130,
    },
    {
        type: COLUMN_TYPES.STATUS,
        title: t`Retry Rate`,
        sortable: true,
        mapping: 'retryStatus',
        width: 120,
    },
    {
        type: COLUMN_TYPES.STATUS,
        title: t`Error Frame`,
        sortable: true,
        mapping: 'errorFrameStatus',
        width: 120,
    },
    {
        type: COLUMN_TYPES.STATUSTREND,
        title: t`Status Trend`,
        sortable: true,
        sortProperty: 'status',
        mapping: 'trends.status.data',
    },
];

const sorters = [{ property: 'status', direction: 'desc' }];

function RadioListPanel(props) {
    const loader = useQueryLoader(dataSource);
    return (
        <ReportingDataTable
            panelProps={props}
            loader={loader}
            columns={columns}
            sorters={sorters}
            deletedMapping="radio.deleted"
        />
    );
}

RadioListPanel.propTypes = panelPropTypes;

RadioListPanel.defaultProps = {
    ...panelPropDefaults,
    title,
    autoHeight: true,
    minContentHeight: 130,
};

export default Panel({
    typeAlias: 'RadioList',
    catalogEntry: {
        category: PANEL_CATEGORIES.RADIO,
        priority: PANEL_PRIORITIES.LIST,
        title,
        description: t`List of wireless access point (AP) radios, showing status trends`,
        previewImage: STATUS_LIST_PANEL_PREVIEW_IMAGE,
        ipm: true,
    },
    filters: {
        required: [timeRangeFilterId],
        optional: [accessPointFilterId, radioBandFilterId, siteFilterId, statusFilterId, wirelessControllerFilterId],
    },
})(RadioListPanel);
